
import { useDraggable } from '@dnd-kit/core'
import { Box, HStack, VStack, Text } from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { Type, Image, Video, Quote, List, Code, Layout, Minus } from 'lucide-react'
import type { BlockDefinition } from '@/types/pageBuilder'

interface DraggableBlockItemProps {
  block: BlockDefinition
  onClick: () => void
}

const iconMap: Record<string, any> = {
  Type,
  Image,
  Video,
  Quote,
  List,
  Code,
  Layout,
  Button: Type, // Fallback
  Minus,
}

export function DraggableBlockItem({ block, onClick }: DraggableBlockItemProps) {
  const colors = useThemeColors()
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `block-type-${block.type}`,
    data: {
      type: block.type,
    },
  })

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined

  const IconComponent = block.icon ? iconMap[block.icon] : null
  const Icon = IconComponent ? <IconComponent size={18} color="currentColor" /> : null

  return (
    <Box
      ref={setNodeRef}
      style={style}
      p={3}
      borderRadius="md"
      cursor={isDragging ? 'grabbing' : 'grab'}
      bg={isDragging ? colors.cardBg : colors.sectionBg}
      border="1px"
      borderColor={isDragging ? 'blue.500' : colors.border}
      _hover={{
        bg: colors.cardBg,
        borderColor: 'blue.500',
        transform: 'translateY(-1px)',
        boxShadow: 'sm',
      }}
      transition="all 0.2s"
      onClick={onClick}
      opacity={isDragging ? 0.5 : 1}
      {...listeners}
      {...attributes}
    >
      <HStack spacing={3}>
        {Icon && (
          <Box color="blue.500" flexShrink={0}>
            {Icon}
          </Box>
        )}
        <VStack spacing={0.5} align="stretch" flex="1" minW={0}>
          <Text fontSize="sm" fontWeight="medium" color={colors.text} noOfLines={1}>
            {block.label}
          </Text>
          {block.description && (
            <Text fontSize="xs" color={colors.textMuted} noOfLines={1}>
              {block.description}
            </Text>
          )}
        </VStack>
      </HStack>
    </Box>
  )
}

